import { useEffect, useState } from 'react'
import useTabVisibility from 'hooks/useTabVisibility'

const MAX_TIMEOUT_MS = 2147483647

interface FlightShortlistExpiry {
  isExpired: boolean
  timeRemaining: string
}

function formatTimeRemaining(remainingMs: number): string {
  if (remainingMs <= 0) return '00:00'
  const minutes = Math.floor(remainingMs / 1000 / 60)
  const seconds = Math.floor((remainingMs / 1000) % 60)
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

function useFlightShortlistExpiry(searchExpiry: number | undefined): FlightShortlistExpiry {
  const tabIsVisible = useTabVisibility()
  const [isExpired, setIsExpired] = useState(() => {
    if (searchExpiry == null) return false
    return searchExpiry <= Date.now()
  })
  const [timeRemaining, setTimeRemaining] = useState(() => {
    if (searchExpiry == null) return ''
    return formatTimeRemaining(searchExpiry - Date.now())
  })

  useEffect(() => {
    if (searchExpiry == null) return

    const update = () => {
      const remaining = searchExpiry - Date.now()
      if (remaining <= 0) {
        setIsExpired(true)
        setTimeRemaining('00:00')
      } else {
        setIsExpired(false)
        setTimeRemaining(formatTimeRemaining(remaining))
      }
    }

    update()

    if (!tabIsVisible) return

    const millisecondsUntilExpiry = searchExpiry - Date.now()
    if (millisecondsUntilExpiry <= 0) return

    const interval = setInterval(update, 1000)
    const timeout = setTimeout(() => {
      setIsExpired(true)
      setTimeRemaining('00:00')
      clearInterval(interval)
    }, Math.min(millisecondsUntilExpiry, MAX_TIMEOUT_MS))

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [searchExpiry, tabIsVisible])

  if (searchExpiry == null) {
    return { isExpired: false, timeRemaining: '' }
  }

  return { isExpired, timeRemaining }
}

export default useFlightShortlistExpiry
